import { type Column, type RowData } from '@tanstack/react-table';
import { Check, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@avanta/shared/components/ui/Command';
import { Popover, PopoverContent, PopoverTrigger } from '@avanta/shared/components/ui/Popover';
import { Separator } from '@avanta/shared/components/ui/Separator';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';

interface TableColumnFilterProps<T extends RowData> {
  column: Column<T, unknown>;
}

export const TableColumnFilter = <T extends RowData>({
  column,
}: TableColumnFilterProps<T>): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.COMMON);
  const [open, setOpen] = useState(false);
  const config = column.columnDef.meta?.columnFilter;

  if (!config || !config.options.length || !column.getCanFilter()) {
    return null;
  }

  const selectedValue = column.getFilterValue();
  const hasSelectedValue = selectedValue !== undefined;

  const setFilterValue = (value: unknown): void => {
    column.setFilterValue(value);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('Common.filter')}
          className={cn(
            'inline-flex size-6 items-center justify-center rounded-sm text-denim-500 outline-none',
            hasSelectedValue && 'text-teal-600'
          )}
        >
          <ChevronDown aria-hidden="true" className="size-4" strokeWidth={2} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"

        className="h-auto max-h-66 w-60 overflow-y-auto rounded-lg border border-plum-100 bg-white p-0 shadow-select-sm"
      >
        <Command loop className="rounded-lg">
          <CommandList className="h-full max-h-full">
            <CommandGroup className="flex flex-col gap-0.5 p-0">
              <CommandItem
                value="__all__"
                onSelect={() => setFilterValue(undefined)}
                className="h-10 shrink-0 justify-between rounded-sm px-4 py-2 text-sm leading-4 text-denim-500 hover:bg-plum-100 hover:text-denim-900 data-[selected=true]:bg-plum-100 data-[selected=true]:text-denim-900"
              >
                <span className={cn(!hasSelectedValue && 'font-medium')}>
                  {config.allLabel ?? t('Common.all')}
                </span>
                {!hasSelectedValue && <Check aria-hidden="true" className="size-4 text-teal-500" />}
              </CommandItem>
              <Separator />
              {config.options.map(option => {
                const isSelected = selectedValue === option.value;
                return (
                  <CommandItem
                    key={String(option.value)}
                    value={String(option.value)}
                    onSelect={() => setFilterValue(option.value)}
                    className="h-10 shrink-0 justify-between rounded-sm px-4 py-2 text-sm leading-4 text-denim-500 hover:bg-plum-100 hover:text-denim-900 data-[selected=true]:bg-plum-100 data-[selected=true]:text-denim-900"
                  >
                    <span className={cn(isSelected && 'font-medium')}>{option.label}</span>
                    {isSelected && <Check aria-hidden="true" className="size-4 text-teal-500" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
